import Loading from "@components/loading/index.jsx";
import moment from "moment";
import React from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { formatDuration, formatMoney } from "@pages/profile-v2/utils";
import AccountInsightsErrorState from "../AccountInsightsErrorState";
import {
	BalanceLabel,
	BalanceRow,
	BalanceRows,
	BalanceValue,
	ChartContainer,
	ChartLabel,
	ChartLegend,
	ChartLegendDot,
	ChartLegendItem,
	ChartLegendLabel,
	ChartLegendValue,
	ChartRow,
	SectionCard,
	SectionTitle,
	StatCard,
	StatGrid,
	StatLabel,
	StatValue,
} from "./elements";
import useController from "./useController";

const AccountInsightsSummarySection = ({ translate }) => {
	const { state, handlers } = useController({ translate });

	if (state.isLoading) {
		return (
			<SectionCard>
				<Loading loading />
			</SectionCard>
		);
	}

	if (state.isError || !state.summary) {
		return (
			<SectionCard>
				<AccountInsightsErrorState
					translate={translate}
					onRetry={handlers.refetch}
				/>
			</SectionCard>
		);
	}

	const { session, financial, balance } = state.summary;

	return (
		<>
			<SectionCard>
				<SectionTitle>{translate("accountInsights.timeOfUse.title")}</SectionTitle>
				<ChartRow $columns="minmax(0, 1fr) 320px">
					<StatGrid>
						<StatCard>
							<StatLabel>
								{translate("accountInsights.timeOfUse.currentSession")}
							</StatLabel>
							<StatValue>
								{formatDuration(session?.currentSessionDurationSeconds, translate)}
							</StatValue>
						</StatCard>
						<StatCard>
							<StatLabel>
								{translate("accountInsights.timeOfUse.sessionStarted")}
							</StatLabel>
							<StatValue>
								{session?.currentSessionStartedAt
									? moment
											.unix(session.currentSessionStartedAt)
											.format("DD.MM.YYYY, HH:mm")
									: "—"}
							</StatValue>
						</StatCard>
						<StatCard>
							<StatLabel>{translate("accountInsights.timeOfUse.today")}</StatLabel>
							<StatValue>
								{formatDuration(session?.totalDurationTodaySeconds, translate)}
							</StatValue>
						</StatCard>
						<StatCard>
							<StatLabel>{translate("accountInsights.timeOfUse.last7Days")}</StatLabel>
							<StatValue>
								{formatDuration(session?.totalDurationLast7DaysSeconds, translate)}
							</StatValue>
						</StatCard>
						<StatCard>
							<StatLabel>{translate("accountInsights.timeOfUse.last30Days")}</StatLabel>
							<StatValue>
								{formatDuration(session?.totalDurationLast30DaysSeconds, translate)}
							</StatValue>
						</StatCard>
					</StatGrid>
					<div>
						<ChartLabel>{translate("accountInsights.charts.usageOverview")}</ChartLabel>
						<ChartContainer $height={220}>
							<Bar
								data={state.summaryUsageChart.data}
								options={state.summaryUsageChart.options}
							/>
						</ChartContainer>
					</div>
				</ChartRow>
			</SectionCard>

			<SectionCard>
				<SectionTitle>{translate("accountInsights.financialLosses.title")}</SectionTitle>
				<StatGrid>
					<StatCard>
						<StatLabel>{translate("accountInsights.financialLosses.today")}</StatLabel>
						<StatValue>{formatMoney(financial?.netLossToday, financial?.currency)}</StatValue>
					</StatCard>
					<StatCard>
						<StatLabel>{translate("accountInsights.financialLosses.last7Days")}</StatLabel>
						<StatValue>
							{formatMoney(financial?.netLossLast7Days, financial?.currency)}
						</StatValue>
					</StatCard>
					<StatCard>
						<StatLabel>{translate("accountInsights.financialLosses.last30Days")}</StatLabel>
						<StatValue>
							{formatMoney(financial?.netLossLast30Days, financial?.currency)}
						</StatValue>
					</StatCard>
					<StatCard>
						<StatLabel>
							{translate("accountInsights.financialLosses.deposits30Days")}
						</StatLabel>
						<StatValue>
							{formatMoney(financial?.totalDepositsLast30Days, financial?.currency)}
						</StatValue>
					</StatCard>
					<StatCard>
						<StatLabel>
							{translate("accountInsights.financialLosses.withdrawals30Days")}
						</StatLabel>
						<StatValue>
							{formatMoney(financial?.totalWithdrawalsLast30Days, financial?.currency)}
						</StatValue>
					</StatCard>
				</StatGrid>
			</SectionCard>

			{balance && (
				<SectionCard>
					<SectionTitle>{translate("accountInsights.balance.title")}</SectionTitle>
					<ChartRow $columns="minmax(0, 1fr) 260px">
						<BalanceRows>
							{state.balanceRows.map(({ key, labelKey }) => {
								const amount = balance[key];

								return (
									<BalanceRow key={key}>
										<BalanceLabel>{translate(labelKey)}</BalanceLabel>
										<BalanceValue>
											{formatMoney(amount?.value, amount?.currency)}
										</BalanceValue>
									</BalanceRow>
								);
							})}
						</BalanceRows>
						{state.balanceChart && (
							<div>
								<ChartLabel>
									{translate("accountInsights.balance.chartLabel")}
								</ChartLabel>
								<ChartContainer $height={220}>
									<Doughnut
										data={state.balanceChart.data}
										options={state.balanceChart.options}
									/>
								</ChartContainer>
								<ChartLegend>
									{state.balanceSegments.map((segment) => (
										<ChartLegendItem key={segment.key}>
											<ChartLegendLabel>
												<ChartLegendDot $color={segment.color} />
												{segment.label}
											</ChartLegendLabel>
											<ChartLegendValue>
												{formatMoney(segment.value, state.balanceCurrency)}
											</ChartLegendValue>
										</ChartLegendItem>
									))}
								</ChartLegend>
							</div>
						)}
					</ChartRow>
				</SectionCard>
			)}
		</>
	);
};

export default AccountInsightsSummarySection;
